import React, { useState, useEffect } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  IconButton,
  Alert,
  CircularProgress,
} from "@mui/material";
import {
  Close as CloseIcon,
  PaymentOutlined as PaymentIcon,
} from "@mui/icons-material";
import { Price, Attendee } from "../../../redux/types/types";
import {
  createManualPaymentApi,
  fetchPricesApi,
  fetchAvailablePricesApi,
} from "../../../utils/api/manualPayment";
import { sortPricesForDisplay } from "../../../utils/priceUtils";
import { PackageSelector } from "../../Payment/PackageSelector";
import Notiflix from "notiflix";
import AttendeeImportantInfoButton from "../../Attendees/AttendeeImportantInfoButton";

interface ManualPaymentDialogProps {
  open: boolean;
  onClose: () => void;
  onManualPaymentCreated: () => void;
  attendee: Attendee;
  classId?: number; // Optional - if provided, filters prices for this specific class
}

interface CartItem {
  priceId: number;
  quantity: number;
  price: Price;
}

const ManualPaymentDialog: React.FC<ManualPaymentDialogProps> = ({
  open,
  onClose,
  onManualPaymentCreated,
  attendee,
  classId,
}) => {
  const [prices, setPrices] = useState<Price[]>([]);
  const [cartItems, setCartItems] = useState<CartItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [fetchingPrices, setFetchingPrices] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const intl = useIntl();

  const fetchPrices = async () => {
    setFetchingPrices(true);
    setError(null);
    try {
      let response;
      if (classId && classId > 0) {
        // Use filtered prices for specific class
        response = await fetchAvailablePricesApi(classId, attendee.id);
      } else {
        // Use all prices for general payment
        response = await fetchPricesApi();
      }

      // Sort prices using utility function
      const sortedPrices = sortPricesForDisplay(response.data);

      setPrices(sortedPrices);
    } catch (error) {
      console.error("Failed to fetch prices:", error);
      setError(intl.formatMessage({ id: "manualPayment.error" }));
    } finally {
      setFetchingPrices(false);
    }
  };

  useEffect(() => {
    if (open) {
      fetchPrices();
      setCartItems([]);
      setError(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, classId, attendee.id]);

  const handleAddToCart = (price: Price, quantity: number) => {
    setCartItems((prevItems) => {
      const existingItemIndex = prevItems.findIndex(
        (item) => item.priceId === price.id,
      );

      if (existingItemIndex !== -1) {
        if (quantity === 0) {
          // Remove item if quantity is 0
          return prevItems.filter((item) => item.priceId !== price.id);
        }
        // Update existing item
        const newItems = [...prevItems];
        newItems[existingItemIndex] = {
          ...newItems[existingItemIndex],
          quantity,
        };
        return newItems;
      } else if (quantity > 0) {
        // Add new item
        return [
          ...prevItems,
          {
            priceId: price.id,
            quantity,
            price,
          },
        ];
      }
      return prevItems;
    });
  };

  const getCartItemQuantity = (priceId: number): number => {
    const item = cartItems.find((item) => item.priceId === priceId);
    return item ? item.quantity : 0;
  };

  const calculateTotal = (): number => {
    return cartItems.reduce(
      (total, item) => total + item.price.amount * item.quantity,
      0,
    );
  };

  const handleCreatePayment = async () => {
    if (cartItems.length === 0) {
      Notiflix.Notify.warning(
        intl.formatMessage({ id: "manualPayment.noPrice" }),
      );
      return;
    }

    setLoading(true);
    setError(null);

    try {
      // For each cart item, create multiple manual payments based on quantity
      for (const item of cartItems) {
        // Create multiple payments for the quantity
        for (let i = 0; i < item.quantity; i++) {
          await createManualPaymentApi({
            priceId: item.priceId,
            attendeeId: attendee.id,
          });
        }
      }

      Notiflix.Notify.success(
        intl.formatMessage({ id: "manualPayment.success" }),
      );

      onManualPaymentCreated();
      handleClose();
    } catch (error) {
      console.error("Failed to create manual payment:", error);
      setError(intl.formatMessage({ id: "manualPayment.error" }));
      Notiflix.Notify.failure(
        intl.formatMessage({ id: "manualPayment.error" }),
      );
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setCartItems([]);
    setError(null);
    onClose();
  };

  const formatPrice = (amount: number) => {
    return (amount / 100).toFixed(2);
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ pr: 6 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <PaymentIcon />
          <FormattedMessage id="manualPayment.title" />
        </Box>
        <IconButton
          aria-label="close"
          onClick={handleClose}
          sx={{
            position: "absolute",
            right: 8,
            top: 8,
            color: (theme) => theme.palette.grey[500],
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers>
        <Typography variant="body1" sx={{ mb: 3 }}>
          <strong>
            <FormattedMessage id="manualPayment.attendee" />:{" "}
            {attendee.firstName} {attendee.lastName}
          </strong>
        </Typography>
        <AttendeeImportantInfoButton attendee={attendee} />

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {fetchingPrices ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
          <PackageSelector
            packages={prices}
            cartItems={cartItems}
            onAddToCart={handleAddToCart}
            getCartItemQuantity={getCartItemQuantity}
            calculateTotal={calculateTotal}
            formatPrice={formatPrice}
          />
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={handleClose} color="secondary">
          <FormattedMessage id="manualPayment.cancel" />
        </Button>
        <Button
          onClick={handleCreatePayment}
          variant="contained"
          color="primary"
          disabled={cartItems.length === 0 || loading || fetchingPrices}
          startIcon={loading ? <CircularProgress size={16} /> : <PaymentIcon />}
        >
          <FormattedMessage id="manualPayment.create" />
          {cartItems.length > 0 && (
            <Typography component="span" sx={{ ml: 1, fontSize: "0.875rem" }}>
              ({formatPrice(calculateTotal())}{" "}
              <FormattedMessage id="manualPayment.currency" />)
            </Typography>
          )}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ManualPaymentDialog;
